import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Shield, BookMarked, PenLine, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { CLASSES, SECTIONS, classLabel } from "@/pages/teacher/teacherUtils";

function StaffSignatureManager({ staffId, hasSignature }) {
  const inputRef = useRef(null);
  const [ver, setVer] = useState(0);
  const [busy, setBusy] = useState(false);
  const [has, setHas] = useState(!!hasSignature);
  const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
  const url = has ? `${apiBase}/profile/signature/${staffId}?v=${ver}` : null;

  const onChange = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!/^image\/(jpeg|png|webp)$/.test(f.type)) { toast.error("Only JPG, PNG or WebP."); return; }
    if (f.size > 10 * 1024 * 1024) { toast.error("Image too large. Max 10 MB."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/staff/${staffId}/signature`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Signature saved (white background removed).");
      setHas(true); setVer(Date.now());
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || "Upload failed");
    } finally { setBusy(false); }
  };
  const onRemove = async () => {
    if (!window.confirm("Remove this teacher's signature?")) return;
    try {
      await api.delete(`/staff/${staffId}/signature`);
      toast.success("Signature removed."); setHas(false); setVer(Date.now());
    } catch (err) { toast.error(apiErr(err.response?.data?.detail) || "Could not remove"); }
  };

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3 bg-slate-50 dark:bg-slate-800/40" data-testid="staff-signature-manager">
      <div className="flex items-center justify-between mb-2">
        <Label className="flex items-center gap-1.5 text-slate-700 dark:text-slate-200"><PenLine className="h-3.5 w-3.5" /> Digital signature</Label>
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => inputRef.current?.click()} disabled={busy} className="text-xs text-sky-600 inline-flex items-center gap-1" data-testid="staff-sig-upload">
            <PenLine className="h-3.5 w-3.5" /> {busy ? "Uploading…" : (has ? "Change" : "Upload")}
          </button>
          {has && (
            <button type="button" onClick={onRemove} className="text-xs text-rose-600 inline-flex items-center gap-1" data-testid="staff-sig-remove">
              <Trash2 className="h-3.5 w-3.5" /> Remove
            </button>
          )}
        </div>
        <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onChange} data-testid="staff-sig-input" />
      </div>
      <div className="rounded-lg border border-dashed border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 p-3 flex items-center justify-center min-h-[72px]">
        {url ? <img src={url} alt="Signature" className="max-h-16 object-contain" /> : <span className="text-xs text-slate-400" data-testid="staff-sig-empty">No signature uploaded yet.</span>}
      </div>
      <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-1.5">Prints on this teacher's report cards & hall tickets (as class teacher). White background is auto-removed.</div>
    </div>
  );
}

export default function TeacherFormDialog({
  open, setOpen, editing, form, set, saving, save,
  toggleClassTeacher, toggleAssignment, toggleSubject, subjectsFor, hasAssignment, subjectsByClass,
}) {
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl rounded-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }}>{editing ? "Edit teacher" : "Add new teacher"}</DialogTitle>
          <DialogDescription>{editing ? "Update teacher information and class assignments." : "Create a new teaching staff or admin account."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="t-first">First name *</Label>
              <Input id="t-first" value={form.firstName} onChange={(e) => set("firstName", e.target.value)} className="h-10 rounded-xl mt-1.5" data-testid="form-first-name"/>
            </div>
            <div>
              <Label htmlFor="t-last">Last name</Label>
              <Input id="t-last" value={form.lastName} onChange={(e) => set("lastName", e.target.value)} className="h-10 rounded-xl mt-1.5" data-testid="form-last-name"/>
            </div>
            <div>
              <Label htmlFor="t-phone">Mobile number * <span className="text-slate-400 font-normal">(login username)</span></Label>
              <div className="relative mt-1.5">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm select-none">+91</span>
                <Input id="t-phone" type="tel" inputMode="numeric" maxLength={10} value={form.phone}
                  onChange={(e) => set("phone", e.target.value.replace(/\D/g, "").slice(0, 10))}
                  placeholder="10-digit mobile" className="h-10 rounded-xl pl-12" data-testid="form-phone"/>
              </div>
            </div>
            <div>
              <Label htmlFor="t-email">Email <span className="text-slate-400 font-normal">(optional)</span></Label>
              <Input id="t-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="h-10 rounded-xl mt-1.5" data-testid="form-email"/>
            </div>
            <div>
              <Label htmlFor="t-role">Role</Label>
              <Select value={form.role} onValueChange={(v) => set("role", v)}>
                <SelectTrigger className="h-10 rounded-xl mt-1.5" data-testid="form-role"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="teacher">Teacher</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="t-gender">Gender <span className="text-slate-400 font-normal">(for "Madam/Sir" in parent messages)</span></Label>
              <Select value={form.gender || "unset"} onValueChange={(v) => set("gender", v === "unset" ? "" : v)}>
                <SelectTrigger className="h-10 rounded-xl mt-1.5" data-testid="form-gender"><SelectValue placeholder="Not set" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unset">Not set</SelectItem>
                  <SelectItem value="female">Female (Madam)</SelectItem>
                  <SelectItem value="male">Male (Sir)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="t-pw">{editing ? "New password (optional)" : "Password *"}</Label>
              <Input id="t-pw" type="password" value={form.password} onChange={(e) => set("password", e.target.value)} className="h-10 rounded-xl mt-1.5" data-testid="form-password"/>
            </div>
          </div>

          {form.role === "teacher" && (
            <div data-testid="assignments-picker" className="space-y-4">
              {/* Class teacher of — placed above assignments */}
              <div>
                <Label className="flex items-center gap-1.5 mb-1.5 text-slate-700 dark:text-slate-200"><Shield className="h-3.5 w-3.5" /> Class teacher of</Label>
                <Select
                  value={(form.class_teacher_of && form.class_teacher_of[0]) ? `${form.class_teacher_of[0].class_name}__${form.class_teacher_of[0].section}` : "none"}
                  onValueChange={(v) => {
                    if (v === "none") { set("class_teacher_of", []); return; }
                    const [cn, s] = v.split("__");
                    toggleClassTeacher(cn, s);
                  }}
                >
                  <SelectTrigger className="h-10 rounded-xl" data-testid="form-class-teacher">
                    <SelectValue placeholder="Not a class teacher" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not a class teacher</SelectItem>
                    {(form.assignments || []).map((a) => (
                      <SelectItem key={`${a.class_name}__${a.section}`} value={`${a.class_name}__${a.section}`}>
                        {classLabel(a.class_name)} · Section {a.section}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="text-xs text-slate-500 dark:text-slate-400 mt-1.5">Pick from the sections assigned below. Only the class teacher can take attendance for that section.</div>
              </div>

              {/* Class assignments with per-section subjects */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="flex items-center gap-1.5 text-slate-700 dark:text-slate-200"><BookMarked className="h-3.5 w-3.5" /> Class assignments & subjects</Label>
                  <span className="text-xs text-slate-500 dark:text-slate-400">{form.assignments.length} section{form.assignments.length === 1 ? "" : "s"}</span>
                </div>
                <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3 bg-slate-50 dark:bg-slate-800/40 space-y-2">
                  {CLASSES.map((cn) => (
                    <div key={cn} className="rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-16 text-sm font-semibold text-slate-700 dark:text-slate-200">{classLabel(cn)}</div>
                        <div className="flex gap-1.5 flex-wrap">
                          {SECTIONS.map((s) => {
                            const on = hasAssignment(cn, s);
                            return (
                              <button
                                type="button"
                                key={s}
                                onClick={() => toggleAssignment(cn, s)}
                                data-testid={`assign-${cn}-${s}`}
                                className={`px-3 h-7 rounded-full text-xs font-medium border transition-colors ${
                                  on
                                    ? "bg-sky-500 border-sky-500 text-white shadow-sm"
                                    : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
                                }`}
                              >
                                Sec {s}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      {/* Subject chips for any selected section in this class */}
                      {SECTIONS.filter((s) => hasAssignment(cn, s)).map((s) => {
                        const selected = subjectsFor(cn, s);
                        const available = subjectsByClass[cn] || [];
                        return (
                          <div key={`subj-${cn}-${s}`} className="ml-16 mt-2 mb-1" data-testid={`subjects-${cn}-${s}`}>
                            <div className="text-[11px] uppercase tracking-wide text-slate-400 dark:text-slate-500 mb-1">Section {s} — subjects</div>
                            <div className="flex gap-1.5 flex-wrap">
                              {available.map((subj) => {
                                const sel = selected.includes(subj);
                                return (
                                  <button
                                    type="button"
                                    key={subj}
                                    onClick={() => toggleSubject(cn, s, subj)}
                                    data-testid={`subject-${cn}-${s}-${subj}`}
                                    className={`px-2.5 h-6 rounded-md text-[11px] font-medium border transition-colors ${
                                      sel
                                        ? "bg-emerald-500 border-emerald-500 text-white"
                                        : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-600 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700"
                                    }`}
                                  >
                                    {subj}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400 mt-2">Tap a section to assign it, then pick the subjects this teacher takes for that section.</div>
              </div>
            </div>
          )}

          {editing && editing.id && (
            <StaffSignatureManager staffId={editing.id} hasSignature={editing.has_signature} />
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full" data-testid="form-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-sky-500 hover:bg-sky-600 rounded-full" data-testid="form-save">
            {saving ? "Saving…" : editing ? "Save changes" : "Create teacher"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
